import { IModel } from './../base/annotation';
import { AdapterContext } from './adapter-context';
import { Named } from '../base/named';
import { Metamodel } from '../base/model';
import { generateIdentity, setIdentity } from '../services/identifier';
import { markProxy } from '../../shared/utils/model-marker-wrapper';
import { LinkedService } from '../linkedservice/linkedservice';

export class ReferenceAdapter {
    public static toResource(named: Named, context: AdapterContext): {} {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const reference = {} as any;
        reference.referenceName = context.adapterConfig.referenceById
            ? Named.OBJECT_ID.templateValue(named) ?? Named.OBJECT_ID.value(named)
            : context.identityProvider.identity(named);
        const model = Metamodel.modelOf(named);
        reference.type = ReferenceAdapter.toType(model);
        return reference;
    }

    public static toType(model: IModel): {} {
        const modelName = model.concept ? model.concept.name : model.name;
        const referenceType = modelName.charAt(0).toUpperCase() + modelName.slice(1) + 'Reference';
        return referenceType;
    }

    public static toModel<T extends Named>(reference: {}, named: T): T {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const areference = reference as any;

        // Use the identity instead of the name to mark the reference
        const model = Metamodel.modelOf(named);
        const resourceType = model.concept ? model.concept.name : model.name;
        named.name = areference.referenceName;
        setIdentity(named, generateIdentity(resourceType, areference.referenceName));

        markProxy(named);
        return named;
    }

    public static toProxyModel<T extends Named>(
        model: IModel,
        reference: {},
        context: AdapterContext,
        nameAttribute: string = 'referenceName'
    ): T {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const areference = reference as any;
        if (model == null || reference == null || !areference[nameAttribute]) {
            return undefined;
        }
        const named = model.createProxy<Named>();
        markProxy(named);
        const resourceType = model.concept ? model.concept.name : model.name;

        named.name = areference[nameAttribute];
        setIdentity(named, generateIdentity(resourceType, areference[nameAttribute]));
        if (context.adapterConfig.tolerateInvalidReference) {
            named.isFakeEntityCreatedFromReference = true;
            named.objectId = named.name;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            named.workspaceObjectId = (reference as any).workspaceObjectId;
            if (named instanceof LinkedService) {
                named.fullyByJsonEditor = true;
                named.tridentArtifactType = named.tridentArtifactType ?? 'Connection';
            }
        }
        return named as T;
    }

    public static toProxyReferenceModel<T extends Named>(model: IModel, identity: string): T {
        const named = model.createProxy<Named>();
        markProxy(named);
        setIdentity(named, identity);
        return named as T;
    }
}
